import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "./App.css";
import { callApi, API_URL, getStoredUser, getStoredToken, setStoredUser, setStoredToken } from "./helper";
import { GoogleLogin } from '@react-oauth/google';
import {
  Button,
  Grid,
  IconButton,
  TextField,
  Paper,
  Box,
  ImageList,
  ImageListItem,
  Typography,
  useTheme,
  useMediaQuery,
  ListItem,
  ListItemText,
  ListItemButton,
  Autocomplete,
} from "@mui/material";
import List from "@mui/material/List";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFile from "@mui/icons-material/AttachFile";
import Send from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTheme as useCustomTheme } from "./App";
const PARTICIPANT_COLOR_MAP = {
  "#8E24AA": "#E1C4E9",
  "#388E3C": "#D1E8D2",
  "#F57C00": "#FFE0B2",
  "#C2185B": "#F8C9D8",
  "#00796B": "#B2DFDB",
  "#689F38": "#E6EE9C",
  "#FBC02D": "#FFF59D",
  "#D32F2F": "#FFCDD2",
  "#303F9F": "#C5CAE9",
  "#558B2F": "#DCEDC8",
  "#D84315": "#FFCCBC",
  "#7B1FA2": "#D1C4E9",
  "#0288D1": "#B3E5FC",
  "#00BCD4": "#B2EBF2",
  "#F9A825": "#FFE082",
  "#5D4037": "#D7CCC8",
  "#455A64": "#CFD8DC",
  "#F57F17": "#FFE082",
  "#9C27B0": "#E1BEE7",
};

const AUTO_MODEL = { slug: "auto", name: "Best Model", short_name: "Best Model", author: "System" };
const MODELS_CACHE_KEY = "parley_models";

const STARTER_PROMPTS = [
  "Ask Nemotron Ultra to explain a complex idea simply",
  "Ask Gemma 4 to brainstorm product ideas",
  "Ask gpt-oss-20b to write a clear professional email",
  "Ask Laguna to help me debug this JavaScript bug",
  "Ask North Mini Code to write a Python script",
  "Ask Nemotron Super to outline a research summary",
  "Ask Ling-3.0-flash to translate this paragraph",
  "Ask Gemma 4 to help me prep for a job interview",
  "Ask Nemotron Ultra to create a study plan",
  "Ask Laguna XS to explain this code step by step",
  "Ask Nemotron Nano VL to describe what is in an image",
  "Generate an image of a cozy rainy-day cafe",
  "Ask gpt-oss-20b to draft a short product review",
  "Ask Nemotron Super to compare two technical approaches",
  "Ask Gemma 4 31B to write a creative short story",
  "Ask North Mini Code to scaffold a React component",
  "Ask Ling-3.0-flash to rewrite this more formally",
  "Ask Nemotron Ultra to make a weekly workout plan",
  "Ask Nemotron Nano Omni to reason through a puzzle",
  "Ask Laguna S to review my SQL query",
  "Ask Gemma 4 to generate marketing taglines",
  "Ask gpt-oss-20b to explain machine learning basics",
  "Ask Nemotron Super to draft technical documentation",
  "Ask Nemotron Ultra to help me plan a weekend trip",
  "Generate an image of a futuristic city at dusk",
  "Ask North Mini Code to teach me Git branching",
  "Ask Ling-3.0-flash to summarize this article",
  "Ask Gemma 4 to create a budget plan template",
  "Ask Nemotron Nano 9B for a quick trivia quiz",
  "Ask Nemotron Ultra to outline a business plan",
];

const ImagePreviewList = ({ imagePreviewUrls, removeImage }) => {
  if (imagePreviewUrls.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        gap: "8px",
        marginBottom: "12px",
        flexWrap: "wrap",
      }}
    >
      {imagePreviewUrls.map((url, i) => (
        <div
          key={i}
          style={{ position: "relative", width: "60px", height: "60px" }}
        >
          <img
            src={url}
            alt={`Preview ${i + 1}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          />
          <IconButton
            onClick={() => removeImage(i)}
            style={{
              position: "absolute",
              top: "-8px",
              right: "-8px",
              width: "20px",
              height: "20px",
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "white",
              minWidth: "20px",
              padding: "0",
            }}
          >
            <CloseIcon style={{ fontSize: "12px" }} />
          </IconButton>
        </div>
      ))}
    </div>
  );
};

const getRandomPrompts = () => {
  const shuffled = [...STARTER_PROMPTS];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, 3);
};

const StarterPrompts = ({ onPromptClick }) => {
  const theme = useTheme();
  const prompts = useMemo(() => getRandomPrompts(), []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        gap: 2,
        px: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: theme.palette.text.secondary,
          mb: 2,
          textAlign: "center",
        }}
      >
        Choose a prompt to get started
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          maxWidth: "600px",
          width: "100%",
        }}
      >
        {prompts.map((prompt, index) => (
          <Button
            key={index}
            variant="outlined"
            onClick={() => onPromptClick(prompt)}
            sx={{
              flex: 1,
              minHeight: "80px",
              textAlign: "left",
              justifyContent: "flex-start",
              p: 2,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.4, textTransform: "none" }}
            >
              {prompt}
            </Typography>
          </Button>
        ))}
      </Box>
    </Box>
  );
};

const MessageList = ({
  messages,
  participantsMap,
  thinkingWho,
  setExpandedImage,
  messagesEndRef,
}) => {
  const theme = useTheme();
  return (
    <List>
      {messages.map((msg, i) => {
        const participant = participantsMap[msg.sender_name];
        const senderLabel =
          msg.sender_type !== "user" && participant
            ? `${msg.sender_name} - ${participant.role} (${participant.short_name})`
            : msg.sender_type !== "user"
              ? msg.sender_name
              : "";
        const bubbleColor = participant ? participant.color : null;
        const isUser = msg.sender_type === "user";
        return (
          <ListItem
            key={msg.id || i}
            sx={{
              justifyContent: isUser ? "flex-end" : "flex-start",
              alignItems: "flex-start",
              px: 1,
              py: 0.5,
              flexDirection: "column",
            }}
          >
            {senderLabel && (
              <Box
                sx={{
                  mb: 0.5,
                  width: { xs: "95%", sm: "70%" },
                  textAlign: isUser ? "right" : "left",
                  alignSelf: isUser ? "flex-end" : "flex-start",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      theme.palette.mode === "light"
                        ? bubbleColor
                        : PARTICIPANT_COLOR_MAP[bubbleColor],
                    fontWeight: "bold",
                  }}
                >
                  {msg.sender_name}
                </Typography>
                {participant && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {"• "} {participant.role} ({participant.short_name})
                  </Typography>
                )}
              </Box>
            )}
            <Paper
              elevation={0}
              sx={{
                px: 1.5,
                py: 0.25,
                maxWidth: { xs: "95%", sm: "70%" },
                backgroundColor:
                  theme.palette.mode === "light" ? "#fbfbfb" : "#0000000f",
                borderRadius: "8px",
                alignSelf: isUser ? "flex-end" : "flex-start",
                overflow: "hidden",
                wordWrap: "break-word",
              }}
            >
              {msg.message && msg.message !== "(image)" && (
                <Box className="message-text">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {msg.message}
                  </ReactMarkdown>
                </Box>
              )}
              {msg.images && msg.images.length > 0 && (
                <ImageList
                  sx={{ width: "100%", height: 200, mb: 1 }}
                  cols={2}
                  rowHeight={200}
                >
                  {msg.images.map((image, j) => (
                    <ImageListItem key={j}>
                      <img
                        src={image}
                        alt={`Uploaded ${j + 1}`}
                        style={{
                          cursor: "pointer",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onClick={() => setExpandedImage(image)}
                        onError={(e) => {
                          console.error("Image failed to load:", image);
                          e.target.style.display = "none";
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Paper>
          </ListItem>
        );
      })}
      {thinkingWho &&
        (() => {
          const thinkingParticipant = participantsMap[thinkingWho];
          const thinkingColor = thinkingParticipant
            ? theme.palette.mode === "light"
              ? thinkingParticipant.color
              : PARTICIPANT_COLOR_MAP[thinkingParticipant.color]
            : "#f0f0f0";
          return (
            <ListItem
              sx={{
                justifyContent: "flex-start",
                alignItems: "flex-start",
                px: 1,
                py: 0.5,
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  mb: 0.5,
                  width: { xs: "95%", sm: "70%" },
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: thinkingColor, fontWeight: "bold" }}
                >
                  {thinkingWho}
                </Typography>
              </Box>
              <Box sx={{ maxWidth: { xs: "95%", sm: "70%" } }}>
                <span className="thinking-text">Thinking</span>
              </Box>
            </ListItem>
          );
        })()}
      <div ref={messagesEndRef} />
    </List>
  );
};

function Chat() {
  const [user, setUser] = useState(null);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantsMap, setParticipantsMap] = useState({});
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [messageInputError, setMessageInputError] = useState("");
  const [userCanSend, setUserCanSend] = useState(true);
  const [thinkingWho, setThinkingWho] = useState(null);
  const [wsReady, setWsReady] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [expandedImage, setExpandedImage] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedModelSlug, setSelectedModelSlug] = useState(null);
  const [availableModels, setAvailableModels] = useState([AUTO_MODEL]);
  const [ipTrackingInfo, setIpTrackingInfo] = useState(null);
  const ws = useRef(null);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const autoRenamedChats = useRef(new Set());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));


  const [dynamicWidth, setDynamicWidth] = useState(100); // Initial min width (adjust as needed)
  const widthRef = useRef(null);

  useEffect(() => {
    if (widthRef.current) {
      // Measure span width + extra for icon/padding
      const measuredWidth = widthRef.current.offsetWidth + 60;
      setDynamicWidth(Math.max(measuredWidth, 100)); // Enforce a min width to avoid collapsing
    }
  }, [selectedModelSlug]); // Re-measure on selection change

  const handlePromptClick = (prompt) => {
    setMessageInput(prompt);
    if (messageInputRef.current) {
      messageInputRef.current.focus();
    }
  };

  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    if (value.length > 1000) {
      setMessageInputError("Message must be 1000 characters or less");
      setMessageInput(value.substring(0, 1000));
    } else {
      setMessageInputError("");
      setMessageInput(value);
    }
  };

  const fetchIpTracking = async () => {
    try {
      const data = await callApi('/api/ip-tracking');
      if (data.success) {
        setIpTrackingInfo(data.data);
      }
    } catch (error) {
      console.error('Error fetching IP tracking:', error);
    }
  };

  const loadAvailableModels = async () => {
    try {
      const cached = sessionStorage.getItem(MODELS_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const models = [AUTO_MODEL];
          for (let i = 0; i < parsed.length; i++) {
            models.push(parsed[i]);
          }
          setAvailableModels(models);
          return;
        }
      }

      const data = await callApi('/api/models');
      if (data.success && Array.isArray(data.models)) {
        sessionStorage.setItem(MODELS_CACHE_KEY, JSON.stringify(data.models));
        const models = [AUTO_MODEL];
        for (let i = 0; i < data.models.length; i++) {
          models.push(data.models[i]);
        }
        setAvailableModels(models);
      }
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  // Initialize user on mount
  useEffect(() => {
    const initializeUser = async () => {
      const existingUser = getStoredUser();
      const existingToken = getStoredToken();

      if (existingUser && existingToken) {
        setUser(existingUser);
        await Promise.all([loadChats(), fetchIpTracking(), loadAvailableModels()]);
      } else {
        const response = await callApi('/api/auth/user');
        if (response.success && response.token) {
          setStoredToken(response.token);
          setStoredUser(response.user);
          setUser(response.user);
          await Promise.all([loadChats(), fetchIpTracking(), loadAvailableModels()]);
        }
      }
    };

    initializeUser();
    // eslint-disable-next-line
  }, []);

  // Function to load and refresh chats
  const loadChats = async () => {
    try {
      const data = await callApi('/api/chats');
      if (data.success) {
        const chatList = [];
        for (let i = 0; i < data.chats.length; i++) {
          chatList.push({ id: data.chats[i].id, name: data.chats[i].name });
        }
        setChats(chatList);
        if (chatList.length > 0) {
          setActiveChat(chatList[0].id);
        } else {
          await addChat();
        }
      }
    } catch (error) {
      console.error('Error loading chats:', error);
    }
  };

  // Sidebar management: collapse on mobile and when switching chats
  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  }, [isMobile, activeChat]);

  const updateParticipants = (newParticipants) => {
    setParticipants(newParticipants);
    const map = {};
    for (let i = 0; i < newParticipants.length; i++) {
      map[newParticipants[i].name] = newParticipants[i];
    }
    setParticipantsMap(map);
  };


  const addChat = async () => {
    await fetchIpTracking();
    const data = await callApi("/api/chats", "POST");
    if (data.success) {
      const newChat = { id: data.chat.id, name: data.chat.name };
      const newChats = [];
      newChats.push(newChat);
      for (let i = 0; i < chats.length; i++) {
        newChats.push(chats[i]);
      }
      setChats(newChats);
      setActiveChat(data.chat.id);
      updateParticipants(data.participants);
      setUserCanSend(true);

      // Auto-focus input after a short delay to ensure it's ready (only on desktop)
      if (!isMobile) {
        setTimeout(() => {
          if (messageInputRef.current) {
            messageInputRef.current.focus();
          }
        }, 100);
      }
    }
  };

  const deleteChat = async (id) => {
    try {
      await callApi(`/api/chats/${id}`, "DELETE");

      const newChats = [];
      for (let i = 0; i < chats.length; i++) {
        if (chats[i].id !== id) {
          newChats.push(chats[i]);
        }
      }
      setChats(newChats);

      if (activeChat === id) {
        if (newChats.length > 0) {
          setActiveChat(newChats[0].id);
        } else {
          setActiveChat(null);
          setMessages([]);
        }
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
    }
  };

  const autoRenameChat = async (chatId) => {
    try {
      const data = await callApi(`/api/chats/${chatId}/auto-rename`, "POST");
      if (data.success) {
        const newChats = [];
        for (let i = 0; i < chats.length; i++) {
          if (chats[i].id === chatId) {
            newChats.push({ ...chats[i], name: data.chat.name });
          } else {
            newChats.push(chats[i]);
          }
        }
        setChats(newChats);
      }
    } catch (error) {
      console.error("Error auto-renaming chat:", error);
    }
  };

  useEffect(() => {
    let reconnectAttempts = 0;

    const connectWebSocket = () => {
      const wsUrl = `${API_URL.replace("http", "ws")}/`;
      console.log("Attempting to connect to:", wsUrl);
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log("WebSocket connected successfully");
        setWsReady(true);
        reconnectAttempts = 0; // Reset on successful connection
      };

      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "message") {
          console.log("Received message via WebSocket:", data.message);
          setThinkingWho(null);
          setMessages((prev) => {
            const newMessages = [];
            for (let i = 0; i < prev.length; i++) {
              newMessages.push(prev[i]);
            }
            newMessages.push(data.message);
            return newMessages;
          });
          if (data.message.userCanSend !== undefined) {
            setUserCanSend(data.message.userCanSend);
          }
        } else if (data.type === "participants_updated") {
          updateParticipants(data.participants);
        } else if (data.type === "user_can_send") {
          setUserCanSend(data.userCanSend);
        } else if (data.type === "thinking") {
          setThinkingWho(data.who);
        }
      };

      ws.current.onclose = (event) => {
        console.log("WebSocket disconnected", event.code, event.reason);
        setWsReady(false);

        // Attempt reconnection with exponential backoff (max 5 attempts)
        if (reconnectAttempts < 5) {
          reconnectAttempts++;
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts - 1),
            30000
          );
          console.log(
            `Attempting to reconnect in ${delay}ms (attempt ${reconnectAttempts}/5)`
          );
          setTimeout(() => {
            console.log("Executing reconnection attempt...");
            connectWebSocket();
          }, delay);
        } else {
          console.log(
            "Max reconnection attempts reached. Please refresh the page."
          );
        }
      };

      ws.current.onerror = (error) => {
        console.log("WebSocket error:", error);
      };
    };

    connectWebSocket();

    return () => {
      if (ws.current) ws.current.close();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activeChat && wsReady && ws.current && user) {
      ws.current.send(JSON.stringify({ 
        type: "join", 
        chatId: activeChat,
        userId: user.id,
        userUuid: user.uuid || user.id
      }));
      fetchMessages(activeChat);
      fetchParticipants(activeChat);
      setUserCanSend(true);
      setThinkingWho(null);
      // Clear auto-renamed tracking for new chat
      autoRenamedChats.current.clear();
    }
    // eslint-disable-next-line
  }, [activeChat, wsReady, user]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, thinkingWho]);

  // UI interactions: auto-focus and keyboard handling
  useEffect(() => {
    // Auto-focus
    if (userCanSend && messageInputRef.current && !isMobile) {
      messageInputRef.current.focus();
    }

    // Escape key handler
    const handleEsc = (e) => {
      if (e.key === "Escape" && expandedImage) {
        setExpandedImage(null);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [userCanSend, isMobile, expandedImage]);

  // Auto-rename chat after 4 messages
  useEffect(() => {
    if (
      messages.length === 4 &&
      activeChat &&
      !autoRenamedChats.current.has(activeChat)
    ) {
      autoRenamedChats.current.add(activeChat);
      autoRenameChat(activeChat);
    }
    // eslint-disable-next-line
  }, [messages.length, activeChat]);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const newSelectedImages = [];
    const newPreviewUrls = [];
    let hasError = false;

    for (let i = 0; i < files.length; i++) {
      if (files[i].size > MAX_FILE_SIZE) {
        setMessageInputError(`File "${files[i].name}" exceeds 10MB limit`);
        hasError = true;
      } else {
        newSelectedImages.push(files[i]);
        newPreviewUrls.push(URL.createObjectURL(files[i]));
      }
    }

    if (newSelectedImages.length > 0) {
      setSelectedImages([...selectedImages, ...newSelectedImages]);
      setImagePreviewUrls([...imagePreviewUrls, ...newPreviewUrls]);
    }

    // Clear error after 5 seconds if set
    if (hasError) {
      setTimeout(() => setMessageInputError(""), 5000);
    }

    // Reset file input
    e.target.value = '';

    // Scroll to bottom to show the new image previews
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const removeImage = (index) => {
    const newSelectedImages = [];
    const newPreviewUrls = [];

    for (let i = 0; i < selectedImages.length; i++) {
      if (i !== index) {
        newSelectedImages.push(selectedImages[i]);
        newPreviewUrls.push(imagePreviewUrls[i]);
      } else {
        URL.revokeObjectURL(imagePreviewUrls[i]);
      }
    }

    setSelectedImages(newSelectedImages);
    setImagePreviewUrls(newPreviewUrls);
  };

  const uploadImages = async () => {
    const uploadedUrls = [];

    for (let i = 0; i < selectedImages.length; i++) {
      const formData = new FormData();
      formData.append("image", selectedImages[i]);
      formData.append("chatId", activeChat);

      try {
        const data = await callApi("/api/upload-image", "POST", formData, true);
        if (data.success) {
          uploadedUrls.push(data.url);
          console.log("Image uploaded:", data.url);
        } else {
          console.error("Image upload failed:", data.error);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }

    return uploadedUrls;
  };

  const sendMessage = async () => {
    if (
      (messageInput.trim() || selectedImages.length > 0) &&
      activeChat &&
      wsReady &&
      ws.current &&
      userCanSend
    ) {
      let imageUrls = [];

      if (selectedImages.length > 0) {
        imageUrls = await uploadImages();
        console.log("All images uploaded, URLs:", imageUrls);
      }

      const messageData = {
        type: "message",
        chatId: activeChat,
        message: messageInput || "(image)",
        sender_name: "User",
        images: imageUrls,
        selectedModelSlug: selectedModelSlug?.slug !== 'auto' && selectedModelSlug?.slug,
        userUuid: user.uuid 
      };

      console.log("Sending message with data:", messageData);
      if (ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify(messageData));
      } else {
        console.error('WebSocket not ready, state:', ws.current.readyState);
      }

      setMessageInput("");
      setMessageInputError("");
      setSelectedImages([]);
      // setSelectedModelSlug(null);

      for (let i = 0; i < imagePreviewUrls.length; i++) {
        URL.revokeObjectURL(imagePreviewUrls[i]);
      }
      setImagePreviewUrls([]);

      setUserCanSend(false);
      setThinkingWho(null);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const data = await callApi(`/api/chats/${chatId}`);
      if (data.success) {
        setMessages(data.messages);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  const fetchParticipants = async (chatId) => {
    try {
      const data = await callApi(`/api/chats/${chatId}/participants`);
      if (data.success) {
        updateParticipants(data.participants);
      }
    } catch (err) {
      console.error("Error fetching participants:", err);
    }
  };

  function ChatList({ chats }) {
    return (
      <div
        className="scrollable-container"
        style={{ height: "100%", maxHeight: "100%" }}
      >
        <List dense sx={{ width: "100%" }}>
          {chats.map((chat, index) => {
            return (
              <Grid key={index} style={{ paddingTop: "4px" }}>
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => deleteChat(chat.id)}
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                  disablePadding
                  sx={{
                    borderRadius: "5px",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      "& .MuiListItemSecondaryAction-root .MuiIconButton-root":
                      {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      setActiveChat(chat.id);
                    }}
                  >
                    <ListItemText id={index} primary={chat.name} />
                  </ListItemButton>
                </ListItem>
              </Grid>
            );
          })}
        </List>
      </div>
    );
  }

  function ParticipantList({ participants }) {
    const theme = useTheme();

    return (
      <div
        className="scrollable-container"
        style={{ height: "100%", maxHeight: "100%" }}
      >
        <List dense sx={{ width: "100%" }}>
          {participants.map((participant, index) => {
            return (
              <Grid key={index} style={{ paddingTop: "4px" }}>
                <ListItem
                  disablePadding
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemButton>
                    <ListItemText
                      id={index}
                      primary={participant.name}
                      secondary={
                        <Box>
                          <Box>{participant.role}</Box>
                          <Box
                            style={{
                              fontSize: "0.75rem",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {participant.model}
                          </Box>
                        </Box>
                      }
                      primaryTypographyProps={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "light"
                            ? participant.color
                            : PARTICIPANT_COLOR_MAP[participant.color],
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </Grid>
            );
          })}
        </List>
      </div>
    );
  }

  const NAV_BAR_PADDING = "12px";
  const { darkMode, toggleTheme } = useCustomTheme();

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const data = await callApi('/api/auth/google', 'POST', {
        id_token: credentialResponse.credential,
      });
      if (data.success && data.token) {
        setStoredToken(data.token);
        setStoredUser(data.user);
        setUser(data.user);
        // Reload chats after login
        await loadChats();
      }
    } catch (error) {
      console.error('Error authenticating:', error);
    }
  };

  const handleGoogleError = () => {
    console.error('Google login error');
  };

  const handleLogout = async () => {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    setChats([]);
    setActiveChat(null);
    setUser(null);
    window.location.href = '/chat'
  };

  let isRateLimited = false;
  let rateLimitMessage = '';
  let rateLimitDisabled = false;

  if (ipTrackingInfo) {
    if (!user || !user.email) {
      if (ipTrackingInfo.guest_limit_exceeded) {
        isRateLimited = true;
        rateLimitMessage = 'You have exceeded the guest limit. Please login to continue.';
        rateLimitDisabled = true;
      }
    } else if (user && user.email) {
      if (ipTrackingInfo.user_limit_exceeded_at) {
        const limitTime = new Date(ipTrackingInfo.user_limit_exceeded_at).getTime();
        const currentTime = Date.now();
        const remainingHours = Math.ceil((limitTime + 24 * 60 * 60 * 1000 - currentTime) / (1000 * 60 * 60));
        
        if (remainingHours > 0) {
          isRateLimited = true;
          rateLimitMessage = `You have reached your daily limit. Please wait ${remainingHours} hours before your limit resets.`;
          rateLimitDisabled = true;
        }
      }
    }
  }

  return (
    <div>
      <div style={{ display: "flex", height: "100vh" }}>
        {/* Mobile overlay when sidebar is open */}
        {isMobile && !sidebarCollapsed && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 999,
            }}
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        <div
          style={{
            width: sidebarCollapsed ? "0px" : isMobile ? "100vw" : "25%",
            minWidth: sidebarCollapsed ? "0px" : isMobile ? "100vw" : "200px",
            height: "100vh",
            backgroundColor: theme.palette.background.sidebar,
            borderRight: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            transition: "all 0.3s ease",
            position: isMobile && !sidebarCollapsed ? "fixed" : "relative",
            zIndex: isMobile && !sidebarCollapsed ? 1000 : "auto",
          }}
        >
          {!sidebarCollapsed && (
            <>
              <IconButton
                onClick={() => setSidebarCollapsed(true)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: "background.paper",
                  boxShadow: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
                size="small"
              >
                <MenuIcon />
              </IconButton>
              <Grid container direction="column" style={{ height: "100%" }}>
                <Grid
                  item
                  style={{
                    height: "30%",
                    padding: NAV_BAR_PADDING,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <h3
                    style={{
                      flexShrink: 0,
                      margin: "0 0 0 0",
                      color: theme.palette.text.primary,
                    }}
                  >
                    People
                  </h3>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ParticipantList participants={participants} />
                  </div>
                </Grid>
                <Grid
                  item
                  style={{
                    height: "63%",
                    padding: NAV_BAR_PADDING,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <h3
                    style={{
                      flexShrink: 0,
                      margin: "0 0 0 0",
                      color: theme.palette.text.primary,
                    }}
                  >
                    Chats
                  </h3>
                  <Button
                    variant="outlined"
                    onClick={addChat}
                    disabled={rateLimitDisabled}
                    sx={{
                      flexShrink: 0,
                      margin: "5px 0px 0px 0px",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      "&:hover": {
                        borderColor:
                          theme.palette.mode === "dark" ? "#ffffff66" : "black",
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    + New Chat
                  </Button>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ChatList chats={chats} />
                  </div>
                </Grid>
                <Grid
                  item
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "background.sidebar",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: NAV_BAR_PADDING,
                    height: "80px",
                    gap: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {user && user.email ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                        minWidth: 0,
                      }}>
                        <Box
                          data-avatar-fallback
                          sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: theme.palette.primary.main,
                            display: user.picture ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: theme.palette.getContrastText(theme.palette.primary.main),
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                            {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                          </Typography>
                        </Box>
                        {user.picture && (
                          <img
                            src={user.picture}
                            alt={user.name || user.email}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.querySelector('[data-avatar-fallback]');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {user.name || 'User'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.65rem',
                              color: theme.palette.text.secondary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        onClick={handleLogout}
                        size="small"
                        sx={{
                          color: theme.palette.text.primary,
                          '&:hover': {
                            color: theme.palette.error.main,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                          },
                        }}
                      >
                        <LogoutIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{
                      '& .google-login-button': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#ffffff',
                        color: theme.palette.mode === 'dark' ? '#000000' : '#000000',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        }
                      }
                    }}>
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme={theme.palette.mode === 'dark' ? 'filled_black' : 'outline'}
                        size="medium"
                        text="signin_with"
                        shape="rectangular"
                      />
                    </Box>
                  )}
                  <IconButton
                    onClick={toggleTheme}
                    sx={{
                      color: theme.palette.text.primary,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                </Grid>
              </Grid>
            </>
          )}
        </div>
        <div
          style={{
            flex: 1,
            height: "100vh",
            backgroundColor: theme.palette.background.default,
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease",
          }}
        >
          {sidebarCollapsed && (
            <IconButton
              onClick={() => setSidebarCollapsed(false)}
              className="mobile-hamburger-fixed"
              sx={{
                position: "fixed",
                top: 16,
                left: 8,
                zIndex: 1300,
                backgroundColor: "background.paper",
                boxShadow: 1,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
              size="small"
            >
              <MenuIcon />
            </IconButton>
          )}
          <div style={{ flex: 1, padding: `${NAV_BAR_PADDING} ${NAV_BAR_PADDING} 0px ${NAV_BAR_PADDING}`, overflow: "auto" }}>
            {isRateLimited ? (
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
                px: 2,
              }}>
                <Paper elevation={0} sx={{
                  px: 2,
                  py: 1,
                  maxWidth: "600px",
                  backgroundColor: theme.palette.mode === "light" ? "#fbfbfb" : "#0000000f",
                  borderRadius: "8px",
                }}>
                  <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                  <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                    {rateLimitMessage}
                  </Typography>
                  </div>
                  {!user || !user.email ? (
                  <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      useOneTap={false}
                      theme={theme.palette.mode === 'dark' ? 'filled_black' : 'outline'}
                      size="medium"
                      text="signin_with"
                      shape="rectangular"
                    />

                  </div>
                  ) : null}
                </Paper>
              </Box>
            ) : (
              messages.length === 0 ? (
                <StarterPrompts onPromptClick={handlePromptClick} />
              ) : (
                <MessageList
                  messages={messages}
                  participantsMap={participantsMap}
                  thinkingWho={thinkingWho}
                  setExpandedImage={setExpandedImage}
                  messagesEndRef={messagesEndRef}
                />
              )
            )}
          </div>
          <div
            style={{
              position: "sticky",
              bottom: 0,
              padding: '10px 0px 10px 0px',
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: isMobile ? "90%" : "75%",
                maxWidth: "900px",
                borderRadius: '20px',
                padding: '16px',
                backgroundColor: '#14141461'
              }}
            >
              <ImagePreviewList
                imagePreviewUrls={imagePreviewUrls}
                removeImage={removeImage}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none",
                        borderBottom: "none" }}
                multiple
                accept="image/*"
                onChange={handleImageSelect}
              />
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <TextField
                    multiline
                    maxRows={6}
                    fullWidth
                    disabled={!userCanSend || rateLimitDisabled}
                    value={messageInput}
                    onChange={handleMessageInputChange}
                    inputRef={messageInputRef}
                    placeholder="Ask Anything"
                    error={!!messageInputError}
                    helperText={messageInputError}
                    variant="standard"
                    sx={{
                    }}

  InputProps={{ disableUnderline: true }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                  />
                </Grid>
                <Grid item>
                  <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Grid item>
                      <Grid container direction="row" spacing={1}>
                        <Grid item style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Button
                            onClick={() => fileInputRef.current.click()}
                            disabled={!userCanSend || rateLimitDisabled}
                            startIcon={<AttachFile />}
                            variant="text"
                            sx={{
                              textTransform: 'none',
                              borderRadius: '20px',
                              fontSize: '13px',
                              color: theme.palette.text.primary, display: 'flex', justifyContent: 'center', alignItems: 'center'
                            }}
                          >
                            Attach
                          </Button>
                        </Grid>
                        <Grid item style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {/*
    Hidden span for measuring text width.
    Style it to match the input's font properties for accuracy.
  */}
                          <span
                            ref={widthRef}
                            style={{
                              position: 'absolute',
                              visibility: 'hidden',
                              whiteSpace: 'pre',
                              fontSize: '13px',
                              // Add fontFamily if your theme overrides the default (e.g., fontFamily: 'Roboto')
                            }}
                          >
                            {(selectedModelSlug?.short_name || availableModels[0].short_name)}
                          </span>

                          <Autocomplete
                            disableClearable={true}
                            options={availableModels}
                            getOptionLabel={(option) => option.short_name}
                            value={selectedModelSlug || availableModels[0]}
                            onChange={(event, newValue) => {
                              setSelectedModelSlug(newValue);
                              messageInputRef.current.focus();
                            }}
                            sx={{
                              // Remove fixed width; use dynamic width instead
                              width: `${dynamicWidth}px`,
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': { border: 'none' },
                                  },
                                  '& .MuiInputBase-input': {
                                    fontSize: '13px',
                                  },
                                  width: '100%',
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={sendMessage}
                        disabled={!userCanSend || rateLimitDisabled}
                        title="Send message"
                      >
                        <Send />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                px: 2,
                maxWidth: "900px",
                width: isMobile ? "90%" : "75%",
                color: "rgba(255,255,255,0.45)",
                fontSize: "11px",
                lineHeight: 1.4,
              }}
            >
              AI can make mistakes. Chat data may be used for training.
              {/* disclaimber */}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;
